import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Container,
  ChartContainer,
  ProgressCourseContainer,
  Chart,
  ProgressBarContainer,
  StateLabel,
  Legend,
  Percentage,
} from './style';
import { chartConfig, getStateColor } from '../../../utils/course-state';
import { DoughnutChart } from '../../components';
import Text from '../Text';
import theme from '../../theme';

const options = {
  cutout: '60%',
  rotation: 180,
};

const mandatoryOptions = {
  ...options,
  borderColor: theme().colors.transparent,
};

const calcPercentages = ({ completed, in_progress, total_courses }) => {
  if (!completed || !in_progress || !total_courses) return [];
  const completedPercentage = (completed / total_courses) * 100;
  const completedDegs = (completed / total_courses) * 360;
  return [
    {
      p: Math.round(completedPercentage),
      deg: completedDegs / 2,
    },
    {
      p: Math.round((in_progress / total_courses) * 100),
      deg: completedDegs + (in_progress / total_courses) * 180,
    },
  ];
};

const calcMandatoryPercentage = ({ completed, total_courses }, mandatory) => {
  if (!(typeof completed === 'number') || !total_courses || !mandatory) {
    return null;
  }
  const mandatoryPercentage = (mandatory / total_courses) * 100;
  const completedDegs = (completed / total_courses) * 360;
  return {
    p: Math.round(mandatoryPercentage),
    deg: completedDegs + (mandatory / total_courses) * 180,
  };
};

const CourseProgress = ({ data = {}, mandatory, circumference = 360 }) => {
  const [showMandatory, setShowMandatory] = useState(false);
  const formattedData = chartConfig(data);
  const formattedMandatory = chartConfig({
    total_courses: data.total_courses,
    mandatory,
    empty: data.total_courses - mandatory,
  });

  useEffect(() => {
    if (mandatory > 0) {
      setTimeout(() => {
        setShowMandatory(true);
      }, 1000);
    }
  }, [mandatory]);

  return (
    <Container>
      <ChartContainer>
        <ProgressCourseContainer>
          <p className="number">{formattedData.total_items}</p>
          <p>Corsi</p>
        </ProgressCourseContainer>
        <Chart>
          <DoughnutChart
            config={formattedData.config}
            customOptions={{ ...options, circumference }}
          />
        </Chart>
        {showMandatory && (
          <Chart>
            <DoughnutChart
              config={formattedMandatory.config}
              customOptions={{
                ...mandatoryOptions,
                rotation: 180 + (360 * data.completed) / data.total_courses,
                circumference,
              }}
            />
          </Chart>
        )}
        {calcPercentages(data).map((item, i) => (
          <Percentage key={`percentage-${i}`} rotate={item.deg}>
            <Text type="b3">{item.p}%</Text>
          </Percentage>
        ))}
        {mandatory > 0 && (
          <Percentage rotate={calcMandatoryPercentage(data, mandatory)?.deg}>
            <Text type="b3">
              {calcMandatoryPercentage(data, mandatory)?.p}%
            </Text>
          </Percentage>
        )}
      </ChartContainer>

      <ProgressBarContainer>
        {mandatory > 0 && (
          <StateLabel>
            <Text type="h3">{mandatory}</Text>
            <Text type="b3">{getStateColor('mandatory').label}</Text>
            <Legend color={getStateColor('mandatory').primary} />
          </StateLabel>
        )}
        {(Object.keys(formattedData.dataset) || []).map((key) => (
          <StateLabel key={key}>
            <Text type="h3">{data[key]}</Text>
            <Text type="b3">{getStateColor(key).label}</Text>
            <Legend color={getStateColor(key).primary} />
          </StateLabel>
        ))}
      </ProgressBarContainer>
    </Container>
  );
};

CourseProgress.propTypes = {
  data: PropTypes.object,
  mandatory: PropTypes.number,
  circumference: PropTypes.number,
};

export default CourseProgress;
